import React, { useState } from 'react';
import { Container, Table, Row } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert'
import { useUser } from '../UserAccess/useUser';
import useDisplayMessage from '../Common/useDisplayMessage';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import useGetUsers from '../Requests/useGetUsers';
import { AddUser } from './AddUser';
import { EditUser } from './EditUser';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import BusinessUserDto from '../Common/dtos/BusinessUserDto';
import BusinessUserUpsertDto from '../Common/dtos/BusinessUserUpsertDto';
import BusinessAdminTemplate from '../UserAccess/BusinessAdminTemplate';
import Confirm from '../Common/Confirm';
import { HttpMethod } from '../Common/useFetch';
import BoldButton from '../Common/BoldButton';

const removeSpec = (businessId?: string, userId?: string): UserRequestSpec => ({
  path: `${baseUserUrl(businessId, userId)}`,
  method: HttpMethod.DELETE,
});

const baseUserUrl = (businessId?: string, userId?: string) => `${baseUrl(businessId)}/${userId}`;

const baseUrl = (businessId?: string) => `/api/businesses/${businessId}/users`;

const UserPage = () => {
  const me = useUser();
  const business = me.selectedBusiness;
  const displayMessage = useDisplayMessage();
  const getUsers = useGetUsers({ autoQuery: business?.administrator });
  const [user, setUser] = useState<BusinessUserDto | null>(null);

  const [addUserVisible, setAddUserVisible] = useState(false);

  const addUserSuccess = (u: BusinessUserUpsertDto) => {
    displayMessage.success(`Added ${u.emailAddress} to business: ${business?.name}`);
    getUsers.query();
    setAddUserVisible(false);
  }

  const addClicked = () => setAddUserVisible(true);

  const addUserDismissed = () => setAddUserVisible(false);

  const [editUserVisible, setEditUserVisible] = useState(false);

  const editClicked = (businessUser: BusinessUserDto) => {
    setUser(businessUser);
    setEditUserVisible(true);
  };

  const editUserDismissed = () => setEditUserVisible(false);

  const editSuccess = () => {
    displayMessage.success(`${user?.emailAddress} updated.`);
    getUsers.query();
    setEditUserVisible(false);
  };

  const [confirmRemoveVisible, setConfirmRemoveVisible] = useState(false);

  const remove = useUserRequest(
    removeSpec(business?.id, user?.id), {
      onComplete: () => setConfirmRemoveVisible(false),
      onSuccess: () => {
        displayMessage.success(`${user?.emailAddress} removed.`);
        getUsers.query();
      },
      onError: (err) => displayMessage.fail(err.message)
    });

  const removeClicked = (businessUser: BusinessUserDto) => {
    setUser(businessUser);
    setConfirmRemoveVisible(true);
  };

  const confirmRemoveDismissed = () => setConfirmRemoveVisible(false);

  const removalConfirmed = () => remove.request(null);

  if (getUsers.isLoading) return <CenteredSpinner />
  if (getUsers.isError) return <h3>{getUsers.error?.message}</h3>

  return (<BusinessAdminTemplate>
    <Container>
      <AddUser
        visible={addUserVisible}
        onSuccess={addUserSuccess}
        user={me}
        onHide={addUserDismissed} />
      {user && <EditUser
        key={user.id}
        visible={editUserVisible}
        businessUser={user}
        user={me}
        onHide={editUserDismissed}
        onSuccess={editSuccess} />}
      <Confirm
        visible={confirmRemoveVisible}
        title='Remove User'
        body={`Are you sure that you want to permanently remove ${user?.emailAddress}`}
        isLoading={remove.isLoading}
        onDismiss={confirmRemoveDismissed}
        onConfirm={removalConfirmed} />
      <Row className="justify-content-between mb-2">
        <h2 className="col-auto ka-blue">Users</h2>
        <BoldButton className="col-auto btn-ghost-primary" onClick={addClicked}>Add User</BoldButton>
      </Row>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <Table bordered>
        <thead>
          <tr>
            <th>User Email</th>
            <th>Administrator</th>
            <th>Error Emails</th>
            <th>Has Signed In</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {getUsers.data?.map(businessUser =>
            <tr key={businessUser.id}>
              <td>{businessUser.emailAddress}</td>
              <td>{businessUser.administrator ? "Yes" : "No"}</td>
              <td>{businessUser.isErrorRecipient ? "Yes" : "No"}</td>
              <td>{businessUser.hasSignedIn ? "Yes" : "No"}</td>
              <td>
                {businessUser.id !== me.id && (<>
                  <BoldButton className="btn-ghost-primary" onClick={() => editClicked(businessUser)}>Edit</BoldButton>
                  {' '}
                  <BoldButton className="btn-ghost-danger" onClick={() => removeClicked(businessUser)}>Remove</BoldButton>
                </>)}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  </BusinessAdminTemplate>);
}

export { UserPage }
